import { Transition } from 'history';
import { useRef, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom-latest';
import { AppBar, Box, BoxSkeleton, Stack, Theme } from '@common-components';
import { useRetrieveCarrierSubmission, useRetrieveSubmissionExtractedData } from 'hooks';
import { PartialSubmission } from 'types';
import { BROKER_ROUTES, CARRIER_SUBMISSION_DETAILS } from 'broker/broker-routes';
import { DirtyRouteGuard } from 'broker/components/DirtyContentGuard/DirtyRouteGuard';
import useSelectedMarket from 'broker/pages/SubmissionQueuePage/useSelectedMarket';
import WorkingOnBanner from 'broker/pages/SubmissionQueuePage/WorkingOnBanner';
import Toolbox from 'broker/pages/SubmissionWorkspacePage/components/Toolbox';
import { ToolboxNavigationSize } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/styles';
import { BrandConfigProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/BrandConfigProvider';
import { SubmissionIdProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/SubmissionIdContext';
import { ModalProvider } from 'broker/pages/SubmissionWorkspacePage/dialogs/modalProvider/modalProvider';
import { UiStoreProvider } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { useWorkspaceResize } from 'broker/pages/SubmissionWorkspacePage/useWorkspaceResize';
import CarrierSubmissionDetailsForm, { CarrierSubmissionDetailsFormHandle } from './CarrierSubmissionDetailsForm';
import Header from './Header';

function ToolboxContainer({ submission }: { submission: PartialSubmission | null }) {
  const { sideBarSizeValue } = useWorkspaceResize({ isTabless: false });

  return (
    <Stack
      bgcolor="common.white"
      width={sideBarSizeValue}
      maxWidth={0.5}
      minWidth={ToolboxNavigationSize}
      overflow="auto"
      sx={{
        transition: (theme: Theme) =>
          theme.transitions.create(['width'], {
            duration: theme.transitions.duration.short,
          }),
      }}
    >
      {submission ? <Toolbox /> : <BoxSkeleton isLoading sx={{ height: 1, width: 1 }} />}
    </Stack>
  );
}

export default function CarrierSubmissionWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: carrierSubmission } = useRetrieveCarrierSubmission({ id: id!, enabled: !!id });
  const { selectedMarket } = useSelectedMarket();

  const submission = carrierSubmission?.submission ?? null;

  const { data: submissionExtractedData } = useRetrieveSubmissionExtractedData({
    submissionId: submission?.id,
  });

  const formRef = useRef<CarrierSubmissionDetailsFormHandle>(null);
  const [isDirty, setIsDirty] = useState(false);
  const skipGuardRef = useRef(false);

  const beforeNavigation = async (transition: Transition) => {
    // allow navigating to routes that include the submission form (like navigating in toolbox tabs inside the page)
    if (transition.location.pathname.includes(`/${CARRIER_SUBMISSION_DETAILS}`)) {
      return true;
    }
    if (skipGuardRef.current) {
      skipGuardRef.current = false;
      return true;
    }
    // Auto-save if form is valid; show dialog if validation fails
    const success = await formRef.current?.validateAndSave();
    return !!success;
  };

  const handleFormSave = async () => {
    skipGuardRef.current = true;
    navigate(`/${BROKER_ROUTES.CARRIER_SUBMISSION_QUEUE}`);
  };

  const handleBack = async () => {
    navigate(`/${BROKER_ROUTES.CARRIER_SUBMISSION_QUEUE}`);
  };

  const onSwitchCarrier = () => navigate(`/${BROKER_ROUTES.SUBMISSION_SERVICES}`);

  return (
    <SubmissionIdProvider submissionId={submission?.id}>
      <ModalProvider>
        <UiStoreProvider>
          <BrandConfigProvider>
            {/* Redirect bare path to submission-details sub-route for Toolbox routing */}
            <Routes>
              <Route index element={<Navigate to={CARRIER_SUBMISSION_DETAILS} replace />} />
            </Routes>
            <DirtyRouteGuard isDirty={isDirty} beforeNavigation={beforeNavigation}>
              <Stack height={1}>
                <AppBar
                  position="static"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'common.white',
                    color: 'text.primary',
                    px: 3,
                    py: 1,
                  }}
                  elevation={0}
                >
                  <WorkingOnBanner selectedMarket={selectedMarket} onSwitchCarrier={onSwitchCarrier} />
                </AppBar>
                <Stack boxShadow={2} zIndex={(theme: Theme) => theme.zIndex.drawer}>
                  {carrierSubmission ? (
                    <Header carrierSubmission={carrierSubmission} onBack={handleBack} />
                  ) : (
                    <BoxSkeleton isLoading sx={{ height: 64, width: 1 }} />
                  )}
                </Stack>
                <Stack bgcolor="background.default" flex={1} direction="row" overflow="hidden">
                  <Stack position="relative" bgcolor="common.white" flex={1} overflow="hidden">
                    {carrierSubmission && (
                      <CarrierSubmissionDetailsForm
                        ref={formRef}
                        carrierSubmission={carrierSubmission}
                        submissionExtractedData={submissionExtractedData ?? undefined}
                        onSave={handleFormSave}
                        onDirtyChange={setIsDirty}
                      />
                    )}
                  </Stack>
                  <Box
                    sx={{
                      width: 4,
                      bgcolor: 'grey.100',
                      borderLeft: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <ToolboxContainer submission={submission} />
                </Stack>
              </Stack>
            </DirtyRouteGuard>
          </BrandConfigProvider>
        </UiStoreProvider>
      </ModalProvider>
    </SubmissionIdProvider>
  );
}
